import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Minus, Plus, ShoppingBag, ChevronRight, Truck, RotateCcw, ShieldCheck } from "lucide-react";
import { PRODUCTS, SIZES, SIZE_GUIDE, PRINT_SPECS, formatPrice } from "@/data/products";
import { useCart } from "@/context/CartContext";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductDetailPage() {
  const { id } = useParams();
  const { add, setOpen } = useCart();
  const [product, setProduct] = useState(PRODUCTS.find((p) => p.id === id) || null);
  const [activeImage, setActiveImage] = useState("main");
  const [color, setColor] = useState(null);
  const [size, setSize] = useState("M");
  const [qty, setQty] = useState(1);

  useEffect(() => {
    axios
      .get(`${API}/products/${id}`)
      .then((r) => {
        const staticProduct = PRODUCTS.find((p) => p.id === id);
        setProduct({ ...r.data, colors: staticProduct?.colors, defaultColor: staticProduct?.defaultColor });
      })
      .catch(() => {});
  }, [id]);

  useEffect(() => {
    if (product?.colors?.length && !color) {
      setColor(product.colors.find((c) => c.name === product.defaultColor) || product.colors[0]);
    }
  }, [product, color]);

  if (!product) {
    return (
      <div className="min-h-screen bg-white font-body text-zinc-900">
        <SiteHeader />
        <div className="mx-auto max-w-7xl px-4 py-32 text-center" data-testid="product-not-found">
          <h1 className="font-heading text-2xl font-bold uppercase">Product not found</h1>
          <Link to="/gb/en/home.html#shop" className="mt-4 inline-block font-body text-sm text-tiger underline">
            Back to the shop
          </Link>
        </div>
        <SiteFooter />
      </div>
    );
  }

  const displayImage = color ? color.image : activeImage === "main" ? product.image : product.secondaryImage;
  const oneSize = Array.isArray(product.variants) && product.variants.length > 0 && product.variants.every((v) => !v.size);
  const effectiveSize = oneSize ? "OS" : size;
  const soldOut = product.stock === 0;
  const lowStock = product.stock != null && product.stock > 0 && product.stock <= 5;

  const handleAdd = () => {
    for (let i = 0; i < qty; i++) add(product, effectiveSize, color);
    setOpen(true);
  };

  return (
    <div className="min-h-screen bg-white font-body text-zinc-900" data-testid="product-detail-page">
      <SiteHeader />
      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-8 lg:px-12 lg:py-16">
        <nav className="mb-8 flex items-center gap-1 font-body text-xs uppercase tracking-widest text-zinc-400" data-testid="product-breadcrumb">
          <Link to="/gb/en/home.html" className="hover:text-tiger">Home</Link>
          <ChevronRight size={12} />
          <Link to="/gb/en/home.html#shop" className="hover:text-tiger">Shop</Link>
          <ChevronRight size={12} />
          <span className="text-zinc-700">{product.title}</span>
        </nav>

        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="relative border border-zinc-200">
              <img
                src={displayImage}
                alt={product.title}
                className="h-[420px] w-full object-cover sm:h-[560px]"
                data-testid="product-main-image"
              />
              <span className="absolute left-4 top-4 bg-tiger px-3 py-1 font-heading text-[10px] font-bold uppercase tracking-widest text-black">
                {product.badge}
              </span>
            </div>
            <div className="mt-4 flex gap-3">
              {["main", "secondary"].map((which) => (
                <button
                  key={which}
                  onClick={() => { setActiveImage(which); setColor(null); }}
                  data-testid={`product-thumb-${which}`}
                  className={`h-20 w-16 overflow-hidden border-2 transition-colors ${!color && activeImage === which ? "border-tiger" : "border-zinc-200 hover:border-zinc-400"}`}
                >
                  <img
                    src={which === "main" ? product.image : product.secondaryImage}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                </button>
              ))}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
            <p className="font-body text-xs font-medium uppercase tracking-[0.3em] text-tiger">{product.category}</p>
            <h1 className="mt-2 font-heading text-2xl font-bold uppercase tracking-tight text-zinc-900 sm:text-3xl lg:text-4xl" data-testid="product-title">
              {product.title}
            </h1>
            <p className="mt-3 font-display text-3xl text-tiger" data-testid="product-price">{formatPrice(product.price)}</p>
            <p className="mt-5 font-body text-sm leading-relaxed text-zinc-600" data-testid="product-description">
              {product.description}
            </p>

            {product.colors?.length > 0 && (
              <div className="mt-8" data-testid="color-selector">
                <p className="mb-3 font-heading text-xs font-bold uppercase tracking-[0.2em] text-zinc-900">
                  Print Colour — <span className="text-tiger" data-testid="color-selected-name">{color?.name}</span>
                </p>
                <div className="flex flex-wrap gap-3">
                  {product.colors.map((c) => (
                    <button
                      key={c.name}
                      onClick={() => setColor(c)}
                      data-testid={`color-swatch-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                      aria-label={`Print colour ${c.name}`}
                      className={`h-10 w-10 rounded-full border-2 transition-all ${
                        color?.name === c.name
                          ? "scale-110 border-tiger ring-2 ring-tiger/30"
                          : "border-zinc-300 hover:border-tiger"
                      }`}
                      style={{ backgroundColor: c.swatch }}
                    />
                  ))}
                </div>
              </div>
            )}

            <div className="mt-8">
              <p className="mb-3 font-heading text-xs font-bold uppercase tracking-[0.2em] text-zinc-900">
                Size — <span className="text-tiger">{oneSize ? "One Size" : size}</span>
              </p>
              {oneSize ? (
                <div data-testid="size-selector">
                  <span className="inline-flex h-11 items-center border border-tiger bg-tiger px-6 font-heading text-sm font-semibold text-black" data-testid="size-btn-os">
                    One Size
                  </span>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2" data-testid="size-selector">
                  {SIZES.map((s) => (
                    <button
                      key={s}
                      onClick={() => setSize(s)}
                      data-testid={`size-btn-${s.toLowerCase()}`}
                      className={`flex h-11 w-12 items-center justify-center border font-heading text-sm font-semibold transition-colors ${
                        size === s
                          ? "border-tiger bg-tiger text-black"
                          : "border-zinc-300 text-zinc-700 hover:border-tiger hover:text-tiger"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-8 flex flex-wrap items-stretch gap-4">
              <div className="flex items-center border border-zinc-300" data-testid="qty-stepper">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="qty-minus-btn" className="flex h-12 w-11 items-center justify-center text-zinc-600 hover:text-tiger">
                  <Minus size={14} />
                </button>
                <span className="w-10 text-center font-heading text-sm font-bold" data-testid="qty-value">{qty}</span>
                <button onClick={() => setQty((q) => Math.min(10, q + 1))} data-testid="qty-plus-btn" className="flex h-12 w-11 items-center justify-center text-zinc-600 hover:text-tiger">
                  <Plus size={14} />
                </button>
              </div>
              <button
                onClick={handleAdd}
                disabled={soldOut}
                data-testid="detail-add-to-cart-btn"
                className={`flex flex-1 items-center justify-center gap-2 px-8 py-3 font-heading text-sm font-bold uppercase tracking-widest transition-colors ${
                  soldOut
                    ? "cursor-not-allowed bg-zinc-200 text-zinc-400"
                    : "bg-zinc-900 text-white hover:bg-tiger hover:text-black"
                }`}
              >
                <ShoppingBag size={16} /> {soldOut ? "Sold Out" : "Add To Cart"}
              </button>
            </div>
            {soldOut && (
              <p className="mt-3 font-body text-xs font-semibold uppercase tracking-widest text-zinc-400" data-testid="sold-out-note">
                Out of stock — restocking soon
              </p>
            )}
            {lowStock && (
              <p className="mt-3 font-body text-xs font-semibold uppercase tracking-widest text-tiger" data-testid="low-stock-note">
                Only {product.stock} left in stock
              </p>
            )}

            <div className="mt-8 grid grid-cols-1 gap-3 border-t border-zinc-200 pt-6 sm:grid-cols-3">
              {[
                [Truck, "2-5 day fulfilment"],
                [RotateCcw, "30-day returns"],
                [ShieldCheck, "240 GSM cotton"],
              ].map(([Icon, label]) => (
                <div key={label} className="flex items-center gap-2 font-body text-xs text-zinc-500">
                  <Icon size={14} className="text-tiger" /> {label}
                </div>
              ))}
            </div>

            <div className="mt-10 border border-zinc-200 bg-fog p-6" data-testid="print-specs-box">
              <h3 className="mb-4 font-heading text-xs font-bold uppercase tracking-[0.2em] text-zinc-900">Print & Fabric Specs</h3>
              <ul className="space-y-2">
                {PRINT_SPECS.map((spec) => (
                  <li key={spec} className="flex items-start gap-2 font-body text-sm text-zinc-600">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 bg-tiger" />
                    {spec}
                  </li>
                ))}
              </ul>
            </div>

            {!oneSize && (
              <div className="mt-6 border border-zinc-200 p-6" data-testid="size-guide-table">
                <h3 className="mb-4 font-heading text-xs font-bold uppercase tracking-[0.2em] text-zinc-900">Size Guide</h3>
                <table className="w-full font-body text-sm">
                  <thead>
                    <tr className="border-b border-zinc-200 text-left text-xs uppercase tracking-widest text-zinc-400">
                      <th className="pb-2">Size</th>
                      <th className="pb-2">Chest</th>
                      <th className="pb-2">Length</th>
                    </tr>
                  </thead>
                  <tbody>
                    {SIZE_GUIDE.map((row) => (
                      <tr key={row.size} className={`border-b border-zinc-100 ${size === row.size ? "font-semibold text-tiger" : "text-zinc-600"}`}>
                        <td className="py-2">{row.size}</td>
                        <td className="py-2">{row.chest}</td>
                        <td className="py-2">{row.length}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </motion.div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
